import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Hook to sync server-side table state (filters, sorting, pagination) with URL search params
 *
 * Similar to usePaginationSearchParams but handles filters and sorting too.
 * This hook ONLY manages URL state - it doesn't handle AG Grid events or data fetching.
 *
 * @param {Object} options
 * @param {boolean} [options.enabled=true] - Whether to sync with URL
 * @param {Object} [options.defaultFilters={}] - Default filter values
 * @param {string} [options.defaultSortBy=''] - Default sort column
 * @param {string} [options.defaultSortOrder='asc'] - Default sort order
 * @param {number} [options.defaultPageSize=25] - Default page size
 * @param {number} [options.defaultPage=1] - Default page number
 * @param {() => void} [options.onUpdateParams] - Optional callback fired after params update
 */
export function useServerSideSearchParams({
  enabled = true,
  defaultFilters = {},
  defaultSortBy = '',
  defaultSortOrder = 'asc',
  defaultPageSize = 25,
  defaultPage = 1,
  onUpdateParams,
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse URL param into typed value based on default value type
  const parseParam = useCallback(
    (key, defaultValue) => {
      if (!enabled) return defaultValue;

      const param = searchParams.get(key);
      if (!param) return defaultValue;

      // Handle arrays (comma-separated)
      if (Array.isArray(defaultValue)) {
        return param.split(',').filter(Boolean);
      }

      // Handle numbers
      if (typeof defaultValue === 'number') {
        return parseInt(param, 10) || defaultValue;
      }

      // Handle booleans
      if (typeof defaultValue === 'boolean') {
        return param === 'true';
      }

      // Handle boolean strings even when default is null/undefined
      if (param === 'true' || param === 'false') {
        return param === 'true';
      }

      return param;
    },
    [enabled, searchParams],
  );

  // Parse all filters from URL
  const filters = useMemo(() => {
    if (!enabled) return defaultFilters;

    const urlFilters = {};
    for (const [key, defaultValue] of Object.entries(defaultFilters)) {
      urlFilters[key] = parseParam(key, defaultValue);
    }
    return urlFilters;
  }, [enabled, defaultFilters, parseParam]);

  // Parse pagination and sorting from URL
  const page = parseParam('page', defaultPage);
  const pageSize = parseParam('pageSize', defaultPageSize);
  const sortBy = parseParam('sortBy', defaultSortBy);
  const sortOrder = parseParam('sortOrder', defaultSortOrder);

  // Update URL params
  const updateParams = useCallback(
    (updates, resetPage = true) => {
      if (!enabled) return;

      setSearchParams(
        prev => {
          const newParams = new URLSearchParams(prev);

          // Apply updates
          Object.entries(updates).forEach(([key, value]) => {
            if (value === undefined || value === null || value === '') {
              newParams.delete(key);
            } else if (Array.isArray(value)) {
              if (value.length > 0) {
                newParams.set(key, value.join(','));
              } else {
                newParams.delete(key);
              }
            } else {
              newParams.set(key, String(value));
            }
          });

          // Reset page to 1 when filters/sort change (unless explicitly updating page)
          if (resetPage && !updates.page) {
            newParams.delete('page'); // Delete to keep URL clean (page=1 is default)
          }

          return newParams;
        },
        { replace: false },
      );

      // Call callback after params update
      onUpdateParams?.();
    },
    [enabled, setSearchParams, onUpdateParams],
  );

  // Reset all params
  const resetParams = useCallback(() => {
    if (!enabled) return;
    setSearchParams({}, { replace: true });
  }, [enabled, setSearchParams]);

  return {
    // Parsed values from URL (or defaults if disabled)
    filters,
    page,
    pageSize,
    sortBy,
    sortOrder,

    // Update functions
    updateParams,
    resetParams,

    // Combined params object (useful for API calls)
    params: useMemo(
      () => ({
        ...filters,
        page,
        pageSize,
        sortBy,
        sortOrder,
      }),
      [filters, page, pageSize, sortBy, sortOrder],
    ),
  };
}
